import { ButtonPrimary, ButtonSecondary, IconButton } from "@nova/component-btn"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { createQuery, QueryableResults } from "@nova/queryable"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import {
    useDeleteMaintenanceTemplate,
    useDuplicateMaintenanceTemplate,
} from "features/pilot-tracking/maintenance/MaintenanceTemplateQueries"
import * as Icons from "@nova/component-icons"
import { Dropdown } from "@nova/component-dropdown"
import { useRouter } from "pages"
import { getGenericWorkspacePage } from "../WorkspaceRoutes"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useState } from "react"
import classNames from "classnames"
import { pluralize } from "@nova/util-strings"

export const MaintenancePage = getGenericWorkspacePage({
    Header: MaintenancePageHeader,
    Content: MaintenancePageContent,
    maxWidth: 1200,
})

const maintenanceTemplatesQuery = createQuery({
    resource: "maintenance_templates",
    select: [
        "id",
        "org_id",
        "name",
        "condition_usage_flights",
        "condition_duration_hours",
        "condition_interval_value",
    ],
    aggregates: [{ field: "aircraft_links.aircraft.id", func: "COUNT", alias: "aircraft_count" }],
})

type MaintenanceTemplates = QueryableResults<typeof maintenanceTemplatesQuery>

function MaintenancePageHeader() {
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()

    return (
        <div className="flex justify-between items-center h-full">
            <div className="text-[1.375rem] telegraf flex items-center">Maintenance</div>
            {isEditor && (
                <router.Link to={{ path: "/fleet/maintenance/template/create" }}>
                    <ButtonPrimary title="Template" LeftIcon={Icons.Plus} />
                </router.Link>
            )}
        </div>
    )
}

function MaintenancePageContent() {
    const router = useRouter("/workspace/:workspaceId")
    const { data, isLoading } = usePilotTrackingQuery(maintenanceTemplatesQuery)
    const { isEditor } = useWorkspacePermissions()

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    if (!data?.length) {
        return (
            <div className="flex flex-col items-center gap-2">
                <h1 className="text-white">Add a Maintenance Template</h1>
                <h5 className="text-gray-100">
                    Create templates to track service intervals for your drone fleet.
                </h5>
                {isEditor && (
                    <router.Link to={{ path: "/fleet/maintenance/template/create" }}>
                        <ButtonPrimary className="mt-4" title="Template" LeftIcon={Icons.Plus} />
                    </router.Link>
                )}
            </div>
        )
    }

    return <MaintenanceTemplateTable templates={data} />
}

const MaintenanceTemplateTable = ({ templates }: { templates: MaintenanceTemplates }) => {
    return (
        <div className="w-full text-white">
            <div className="flex flex-col gap-2">
                {templates.map((t) => (
                    <MaintenanceTemplateRow key={t.id} template={t} />
                ))}
            </div>
        </div>
    )
}

const MaintenanceTemplateRow = ({ template }: { template: MaintenanceTemplates[number] }) => {
    const router = useRouter("/workspace/:workspaceId")
    const { isEditor } = useWorkspacePermissions()
    const { deleteMaintenanceTemplate } = useDeleteMaintenanceTemplate()
    const { duplicateMaintenanceTemplate } = useDuplicateMaintenanceTemplate()
    const confirmationModal = useConfirmationModalContext()
    const dispatch = useAppDispatch()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const handleDelete = () => {
        confirmationModal.confirm({
            RenderComponent: getGenericConfirmationModalComponent({
                title: "Delete Template?",
                message: `Delete ${template.name}?`,
                confirmText: "Delete Template",
            }),
            asyncConfirmCallback: async () => {
                try {
                    await deleteMaintenanceTemplate(template.id)
                    dispatch(
                        setGlobalNotificationContext({
                            message: `${template.name} has been deleted.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 5000,
                        })
                    )
                } catch {
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Failed to delete ${template.name}. Please try again.`,
                            status: GlobalNotificationStatus.error,
                            timeoutInMs: 5000,
                        })
                    )
                }
            },
        })
    }

    const aircraftCount = Number(template.aircraft_count)
    const appliesTo =
        aircraftCount === 0 ? "All drones" : pluralize({ count: aircraftCount, str: "drone" })

    const intervalDays = template.condition_interval_value

    return (
        <div
            className={`group flex items-center gap-11 px-6 py-4 border border-white-100 rounded-lg transition-colors ${isDropdownOpen ? "bg-white-100" : "hover:bg-white-100"}`}
        >
            <div className="grow">
                <h4 className="text-gray-0">{template.name}</h4>
                <small className="text-gray-300">Applies to: {appliesTo}</small>
            </div>
            <div className="flex items-center gap-10 overflow-hidden">
                <div className="flex flex-col gap-1 items-end">
                    <h3 className="text-gray-100">
                        {template.condition_usage_flights ?? (
                            <span className="text-gray-500">—</span>
                        )}
                    </h3>
                    <small className="font-mono text-gray-400">Flights</small>
                </div>
                <div className="flex flex-col gap-1 items-end">
                    <h3 className="text-gray-100">
                        {template.condition_duration_hours ?? (
                            <span className="text-gray-500">—</span>
                        )}
                    </h3>
                    <small className="font-mono text-gray-400">Hours</small>
                </div>
                <div className="flex flex-col gap-1 items-end">
                    <h3 className="text-gray-100">
                        {intervalDays ?? <span className="text-gray-500">—</span>}
                    </h3>
                    <small className="font-mono text-gray-400">Days</small>
                </div>
                {isEditor && (
                    <div
                        className={classNames(
                            "flex items-center gap-2 transition-[width,opacity]",
                            {
                                "w-[100px]": isDropdownOpen,
                                "opacity-0 group-hover:opacity-100 w-0 group-hover:w-[100px]":
                                    !isDropdownOpen,
                            }
                        )}
                    >
                        <router.Link
                            to={{
                                path: "/fleet/maintenance/template/:templateId",
                                params: { templateId: template.id },
                            }}
                        >
                            <ButtonSecondary title="Edit" LeftIcon={Icons.Edit} />
                        </router.Link>
                        <Dropdown onOpenChange={setIsDropdownOpen}>
                            <Dropdown.Trigger>
                                <IconButton className="text-gray-100">
                                    <Icons.SmallIcon Icon={Icons.MoreVert} />
                                </IconButton>
                            </Dropdown.Trigger>
                            <Dropdown.Menu position="bottom-right">
                                <Dropdown.ButtonItem
                                    className="flex items-center gap-3"
                                    onClick={async () => {
                                        try {
                                            await duplicateMaintenanceTemplate(template.id)
                                            dispatch(
                                                setGlobalNotificationContext({
                                                    message: `${template.name} has been duplicated.`,
                                                    status: GlobalNotificationStatus.success,
                                                    timeoutInMs: 5000,
                                                })
                                            )
                                        } catch {
                                            dispatch(
                                                setGlobalNotificationContext({
                                                    message: `Failed to duplicate ${template.name}. Please try again.`,
                                                    status: GlobalNotificationStatus.error,
                                                    timeoutInMs: 5000,
                                                })
                                            )
                                        }
                                    }}
                                >
                                    <Icons.SmallIcon Icon={Icons.ContentCopy} />
                                    Duplicate
                                </Dropdown.ButtonItem>
                                <Dropdown.ButtonItem
                                    className="flex items-center gap-3 !text-red-500"
                                    onClick={handleDelete}
                                >
                                    <Icons.SmallIcon Icon={Icons.Trash} />
                                    Delete
                                </Dropdown.ButtonItem>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                )}
            </div>
        </div>
    )
}
